import { pitchViewBox, visibleRegion, type CustomPitch, type PitchView } from './pitchArea';
import type { PitchSpec } from './pitchSpecs';

interface DrillPitchSvgProps {
  spec: PitchSpec;
  view: PitchView;
  custom?: CustomPitch | null;
}

/**
 * Pitch backdrop. The visible canvas is the WORKING AREA:
 *
 *   - view='full'             → full age-group pitch with all markings
 *   - view='half_attacking'   → attacking half only, with one penalty area
 *   - view='half_defending'   → defending half only
 *   - view='quarter'          → attacking-left quadrant
 *   - view='custom'           → a plain rectangular grid at custom dims
 *
 * Markings + corner arcs are skipped for non-full views so the coach
 * gets a clean working surface to lay out a rondo / SSG / pattern.
 */
export function DrillPitchSvg({ spec, view, custom }: DrillPitchSvgProps): React.JSX.Element {
  const region = visibleRegion(spec, view, custom);
  const STROKE = 'rgba(255, 255, 255, 0.32)';
  const SW = Math.min(0.3, region.width * 0.012);
  const isFull = view === 'full';
  const isHalf = view === 'half_attacking' || view === 'half_defending';
  const isCustom = view === 'custom';

  const halfL = spec.length / 2;
  const halfW = spec.width / 2;

  // Penalty area + goal area rectangles (one each end).
  const paLeft = { x: -halfL, y: -spec.penaltyAreaWidth / 2, w: spec.penaltyAreaLength, h: spec.penaltyAreaWidth };
  const paRight = { x: halfL - spec.penaltyAreaLength, y: -spec.penaltyAreaWidth / 2, w: spec.penaltyAreaLength, h: spec.penaltyAreaWidth };
  const gaLeft = { x: -halfL, y: -spec.goalAreaWidth / 2, w: spec.goalAreaLength, h: spec.goalAreaWidth };
  const gaRight = { x: halfL - spec.goalAreaLength, y: -spec.goalAreaWidth / 2, w: spec.goalAreaLength, h: spec.goalAreaWidth };
  const hasGoalArea = spec.goalAreaLength > 0 && spec.goalAreaWidth > 0;
  const GOAL_DEPTH = Math.min(1.5, region.length * 0.04);

  // The working-area rectangle in spec coords — this IS the rendered region.
  const workRect = {
    x: region.cx - region.length / 2,
    y: region.cy - region.width / 2,
    w: region.length,
    h: region.width,
  };

  return (
    <svg
      viewBox={pitchViewBox(spec, view, custom)}
      preserveAspectRatio="xMidYMid meet"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        background:
          'radial-gradient(70% 70% at 50% 45%, rgba(74, 222, 128, 0.06), transparent 70%),' +
          'linear-gradient(180deg, #0a1f15 0%, #061008 60%, #030504 100%)',
      }}
      role="img"
      aria-label={`Pitch — ${spec.label}${isFull ? '' : ` · ${view}`}`}
    >
      {/* Pitch surface (working area). */}
      <rect
        x={workRect.x} y={workRect.y}
        width={workRect.w} height={workRect.h}
        fill="rgba(13, 53, 32, 0.55)"
        stroke={STROKE}
        strokeWidth={SW + 0.15}
      />

      {/* Centre spot + halfway line — only for full + half views. */}
      {(isFull || isHalf) && (
        <>
          {isFull && (
            <line x1={0} y1={-halfW} x2={0} y2={halfW} stroke={STROKE} strokeWidth={SW} />
          )}
          <circle cx={0} cy={0} r={spec.centreCircleRadius} fill="none" stroke={STROKE} strokeWidth={SW}
            clipPath={`url(#drill-pitch-clip)`} />
          <circle cx={0} cy={0} r={SW * 1.2} fill="rgba(255,255,255,0.5)" />
        </>
      )}

      {/* Penalty + goal areas. */}
      {isFull && (
        <>
          <rect {...rectProps(paLeft)} fill="none" stroke={STROKE} strokeWidth={SW} />
          <rect {...rectProps(paRight)} fill="none" stroke={STROKE} strokeWidth={SW} />
          {hasGoalArea && <>
            <rect {...rectProps(gaLeft)} fill="none" stroke={STROKE} strokeWidth={SW} />
            <rect {...rectProps(gaRight)} fill="none" stroke={STROKE} strokeWidth={SW} />
          </>}
          <circle cx={-halfL + spec.penaltySpotDistance} cy={0} r={SW * 1.1} fill="rgba(255,255,255,0.4)" />
          <circle cx={halfL - spec.penaltySpotDistance} cy={0} r={SW * 1.1} fill="rgba(255,255,255,0.4)" />
          <CornerArc x={-halfL} y={-halfW} dx={1} dy={1} stroke={STROKE} sw={SW} />
          <CornerArc x={-halfL} y={halfW} dx={1} dy={-1} stroke={STROKE} sw={SW} />
          <CornerArc x={halfL} y={-halfW} dx={-1} dy={1} stroke={STROKE} sw={SW} />
          <CornerArc x={halfL} y={halfW} dx={-1} dy={-1} stroke={STROKE} sw={SW} />
          <GoalFrame x={-halfL - GOAL_DEPTH} y={-spec.goalWidth / 2} w={GOAL_DEPTH} h={spec.goalWidth} stroke={STROKE} sw={SW} />
          <GoalFrame x={halfL} y={-spec.goalWidth / 2} w={GOAL_DEPTH} h={spec.goalWidth} stroke={STROKE} sw={SW} />
        </>
      )}

      {/* Half views — show only the goal + penalty area that's in-frame. */}
      {view === 'half_attacking' && (
        <>
          <rect {...rectProps(paLeft)} fill="none" stroke={STROKE} strokeWidth={SW} />
          {hasGoalArea && <rect {...rectProps(gaLeft)} fill="none" stroke={STROKE} strokeWidth={SW} />}
          <circle cx={-halfL + spec.penaltySpotDistance} cy={0} r={SW * 1.1} fill="rgba(255,255,255,0.4)" />
          <GoalFrame x={-halfL - GOAL_DEPTH} y={-spec.goalWidth / 2} w={GOAL_DEPTH} h={spec.goalWidth} stroke={STROKE} sw={SW} />
        </>
      )}
      {view === 'half_defending' && (
        <>
          <rect {...rectProps(paRight)} fill="none" stroke={STROKE} strokeWidth={SW} />
          {hasGoalArea && <rect {...rectProps(gaRight)} fill="none" stroke={STROKE} strokeWidth={SW} />}
          <circle cx={halfL - spec.penaltySpotDistance} cy={0} r={SW * 1.1} fill="rgba(255,255,255,0.4)" />
          <GoalFrame x={halfL} y={-spec.goalWidth / 2} w={GOAL_DEPTH} h={spec.goalWidth} stroke={STROKE} sw={SW} />
        </>
      )}

      {/* Custom grid — just a labeled rectangle with corner markers. */}
      {isCustom && (
        <>
          {[
            { cx: workRect.x, cy: workRect.y },
            { cx: workRect.x + workRect.w, cy: workRect.y },
            { cx: workRect.x, cy: workRect.y + workRect.h },
            { cx: workRect.x + workRect.w, cy: workRect.y + workRect.h },
          ].map((p, i) => (
            <circle key={i} cx={p.cx} cy={p.cy} r={SW * 1.5} fill="rgba(255,255,255,0.5)" />
          ))}
        </>
      )}

      {/* Mask used by the centre-circle clip (so half-pitch views don't
          render the full circle past the half line). */}
      <defs>
        <clipPath id="drill-pitch-clip">
          <rect x={workRect.x} y={workRect.y} width={workRect.w} height={workRect.h} />
        </clipPath>
      </defs>
    </svg>
  );
}

function rectProps(r: { x: number; y: number; w: number; h: number }): { x: number; y: number; width: number; height: number } {
  return { x: r.x, y: r.y, width: r.w, height: r.h };
}

function CornerArc({ x, y, dx, dy, stroke, sw }: { x: number; y: number; dx: number; dy: number; stroke: string; sw: number }): React.JSX.Element {
  const start = { x: x, y: y + dy };
  const end = { x: x + dx, y: y };
  const sweep = dx * dy > 0 ? 1 : 0;
  return (
    <path
      d={`M ${start.x} ${start.y} A 1 1 0 0 ${sweep} ${end.x} ${end.y}`}
      fill="none"
      stroke={stroke}
      strokeWidth={sw}
    />
  );
}

function GoalFrame({ x, y, w, h, stroke, sw }: { x: number; y: number; w: number; h: number; stroke: string; sw: number }): React.JSX.Element {
  return (
    <rect
      x={x} y={y} width={w} height={h}
      fill="rgba(255,255,255,0.05)"
      stroke={stroke}
      strokeWidth={sw}
    />
  );
}
